import type { ThemeConfig } from '../models/theme.model';
import type { ThemeMode } from '../services/theme.service';

/** Default filter control surface colors (aligned with `jepto-date-range-picker`). */
export const FILTER_CONTROL_COLORS_LIGHT = {
	background: '#ffffff',
	/** Stronger outline (legacy / date-range). */
	borderPrimary: '#7d818a',
	/** Dropdown filter trigger outline — Figma `border/secondary`. */
	borderSecondary: '#e2e3e5',
	textPrimary: 'rgba(15, 21, 38, 0.82)',
	textSecondary: 'rgba(15, 21, 38, 0.54)',
	hoverMix: 'rgba(15, 21, 38, 0.06)',
	accent: '#651fff',
} as const;

export const FILTER_CONTROL_COLORS_DARK = {
	background: '#1a2036',
	borderPrimary: '#646a7f',
	borderSecondary: '#363b4e',
	textPrimary: 'rgba(255, 255, 255, 0.87)',
	textSecondary: 'rgba(255, 255, 255, 0.6)',
	hoverMix: 'rgba(255, 255, 255, 0.08)',
	accent: '#ab7aff',
} as const;

export interface FilterControlThemeColors {
	background: string;
	borderPrimary: string;
	borderSecondary: string;
	textPrimary: string;
	textSecondary: string;
	hoverMix: string;
	accent: string;
}

export function resolveFilterControlIsDark(
	themeMode: ThemeMode,
	activeThemeMode: 'light' | 'dark'
): boolean {
	if (themeMode === 'dark') {
		return true;
	}
	if (themeMode === 'light') {
		return false;
	}
	return activeThemeMode === 'dark';
}

export function resolveFilterControlThemeColors(
	isDark: boolean,
	themeConfig: ThemeConfig | null | undefined
): FilterControlThemeColors {
	const defaults = isDark
		? FILTER_CONTROL_COLORS_DARK
		: FILTER_CONTROL_COLORS_LIGHT;

	if (!themeConfig) {
		return { ...defaults };
	}

	const mode = isDark ? themeConfig.colors.dark : themeConfig.colors.light;
	return {
		background:
			mode.card?.background ?? mode.background ?? defaults.background,
		borderPrimary:
			mode.border?.primary ??
			mode.border?.secondary ??
			defaults.borderPrimary,
		borderSecondary:
			mode.border?.secondary ??
			mode.border?.primary ??
			defaults.borderSecondary,
		textPrimary: mode.text?.primary ?? defaults.textPrimary,
		textSecondary: mode.text?.secondary ?? defaults.textSecondary,
		hoverMix: defaults.hoverMix,
		accent: mode.text?.link ?? defaults.accent,
	};
}
